import React, { Component, useEffect } from 'react';
import { bool, string } from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { FormSpy, Field, Form as FinalForm } from 'react-final-form';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';
import ReactCrop, { convertToPixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { ensureCurrentUser } from '../../util/data';
import { propTypes } from '../../util/types';
import { isUploadImageOverLimitError } from '../../util/errors';
import { Prompt } from 'react-router-dom';
import arrayMutators from 'final-form-arrays';
import { Form, Avatar, Button, ImageFromFile, IconSpinner, Modal } from '../../components';

import css from './ProfileSettingsForm.css';

// const ReactQuill = typeof window === 'object' ? require('react-quill') : () => false;

const ACCEPT_IMAGES = 'image/*';
const UPLOAD_CHANGE_DELAY = 2000; // Show spinner so that browser has time to load img srcset
const MAX_MOBILE_SCREEN_WIDTH = 962;

function canvasPreview(image, canvas, crop) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const pixelRatio = window.devicePixelRatio || 1;

  canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
  canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

  ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
  ctx.imageSmoothingQuality = 'high';

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width * scaleX,
    crop.height * scaleY
  );
}

function getCenteredAspectCrop(mediaWidth, mediaHeight, aspect) {
  let cropWidth, cropHeight;
  if (mediaWidth / mediaHeight > aspect) {
    cropHeight = mediaHeight;
    cropWidth = cropHeight * aspect;
  } else {
    cropWidth = mediaWidth;
    cropHeight = cropWidth / aspect;
  }
  const x = ((mediaWidth - cropWidth) / 2 / mediaWidth) * 100;
  const y = ((mediaHeight - cropHeight) / 2 / mediaHeight) * 100;
  const width = (cropWidth / mediaWidth) * 100;
  const height = (cropHeight / mediaHeight) * 100;
  return {
    unit: '%',
    x,
    y,
    width,
    height,
    aspect,
  };
}

function CropImage({
  src,
  onCropChange,
  onImageLoaded,
  crop,
  setCrop,
  completedCrop,
  setCompletedCrop,
  imgRef,
  previewCanvasRef,
}) {
  // Only square aspect for avatar
  const aspect = 1;

  function handleImageLoad(e) {
    const { width, height } = e.currentTarget;
    const centeredCrop = getCenteredAspectCrop(width, height, aspect);
    setCrop(centeredCrop);
    setCompletedCrop(convertToPixelCrop(centeredCrop, width, height));
    onImageLoaded(e.currentTarget);
  }

  useEffect(() => {
    if (
      completedCrop?.width &&
      completedCrop?.height &&
      imgRef.current &&
      previewCanvasRef.current
    ) {
      canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop);
    }
  }, [completedCrop, imgRef, previewCanvasRef]);

  return (
    <div>
      <ReactCrop
        crop={crop}
        aspect={aspect}
        onChange={(_, percentCrop) => {
          setCrop(percentCrop);
          onCropChange(percentCrop);
        }}
        onComplete={c => setCompletedCrop(c)}
        keepSelection={true}
        circularCrop={true}
      >
        <img
          ref={imgRef}
          src={src}
          alt="Crop preview"
          onLoad={handleImageLoad}
          style={{ maxWidth: '100%', maxHeight: '65vh' }}
        />
      </ReactCrop>
      <canvas
        ref={previewCanvasRef}
        style={{
          display: 'none',
        }}
      />
    </div>
  );
}

class ProfileSettingsFormComponent extends Component {
  constructor(props) {
    super(props);

    this.uploadDelayTimeoutId = null;
    this.imageReader = new FileReader();
    this.imageReader.addEventListener(
      'load',
      () => {
        // convert image file to base64 string
        this.setState({ cropImageSrc: this.imageReader.result });
      },
      false
    );
    this.state = {
      uploadDelay: false,
      isSelectionModalOpen: false,
      cropImageSrc: null,
      crop: null,
      completedCrop: null,
      imageCropElement: null,
    };
    this.submittedValues = {};

    this.onCropChange = this.onCropChange.bind(this);
    this.handleSubmitSelection = this.handleSubmitSelection.bind(this);
    this.onImageLoaded = this.onImageLoaded.bind(this);
    this.onDirtyChange = this.onDirtyChange.bind(this);
    this.handleSetImageToBeDeleted = this.handleSetImageToBeDeleted.bind(this);
    this.profileImageChanged = false;

    this.imgRef = React.createRef();
    this.previewCanvasRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    // Upload delay is additional time window where Avatar is added to the DOM,
    // but not yet visible (time to load image URL from srcset)
    if (prevProps.uploadInProgress && !this.props.uploadInProgress) {
      this.setState({ uploadDelay: true });
      this.uploadDelayTimeoutId = window.setTimeout(() => {
        this.setState({ uploadDelay: false });
      }, UPLOAD_CHANGE_DELAY);
    }
  }

  componentDidMount() {
    window.addEventListener('beforeunload', this.onUnload);
  }

  componentWillUnmount() {
    window.clearTimeout(this.uploadDelayTimeoutId);
    window.removeEventListener('beforeunload', this.onUnload);
  }

  onCropChange(newCrop) {
    this.setState({ crop: newCrop });
  }

  onImageLoaded(imageEl) {
    this.setState({ imageCropElement: imageEl });
  }

  onDirtyChange({ dirty }) {
    setTimeout(() => this.setState({ formIsDirty: dirty }), 0);
  }

  /**
   * @param {HTMLImageElement} image - Image File Object
   * @param {Object} crop - crop Object
   * @param {String} fileName - Name of the returned file in Promise
   */
  getCroppedImg(image, crop, fileName) {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );

    // As Base64 string
    // const base64Image = canvas.toDataURL('image/jpeg');

    // As a blob
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        blob => {
          blob.name = fileName;
          resolve(blob);
        },
        'image/jpeg',
        1
      );
    });
  }

  handleSubmitSelection() {
    const { completedCrop } = this.state;
    const { onImageUpload } = this.props;
    const image = this.imgRef.current;
    const previewCanvas = this.previewCanvasRef.current;

    if (!image || !previewCanvas || !completedCrop) {
      return;
    }

    const tempId = `${image.name || 'avatar'}_${Date.now()}`;

    this.setState({ isSelectionModalOpen: false });

    // Create cropped image file from canvas
    previewCanvas.toBlob(
      blob => {
        if (blob) {
          blob.name = tempId;
          onImageUpload({ id: tempId, file: blob });
        }
      },
      'image/jpeg',
      1
    );
  }

  onUnload = e => {
    if (this.state.formIsDirty) {
      e.preventDefault();
      e.returnValue = '';
    }
  };

  handleSetImageToBeDeleted(form) {
    form.change('profileImage', null);
    form.blur(`profileImage`);
    this.props.setImageToBeDeleted(true);
  }

  render() {
    return (
      <FinalForm
        {...this.props}
        mutators={{
          ...arrayMutators,
        }}
        render={fieldRenderProps => {
          const {
            className,
            currentUser,
            handleSubmit,
            intl,
            invalid,
            onImageUpload,
            profileImage,
            rootClassName,
            updateInProgress,
            updateProfileError,
            uploadImageError,
            uploadInProgress,
            form,
            values,
            nameLocationWorkingMethod,
            bioWhoAmI,
            links,
            codeOfConduct,
            hourlyFeeIdealCustomer,
            topSkills,
            toolsContainer,
            hobbies,
            languages,
            workExperience,
            workedWith,
            portfolioWorksContainer,
            education,
            viewport,
            showContainers,
            isValidCustomer,
            isValidProvider,
          } = fieldRenderProps;

          const user = ensureCurrentUser(currentUser);

          const dirtyFormAlert = intl.formatMessage({
            id: 'DirtyFormAlert',
          });

          const uploadingOverlay =
            uploadInProgress || this.state.uploadDelay ? (
              <div className={css.uploadingImageOverlay}>
                <IconSpinner />
              </div>
            ) : null;

          const hasUploadError = !!uploadImageError && !uploadInProgress;
          const errorClasses = classNames({ [css.avatarUploadError]: hasUploadError });
          const transientUserProfileImage = profileImage.uploadedImage || user.profileImage;
          const transientUser = { ...user, profileImage: transientUserProfileImage };

          // Ensure that file exists if imageFromFile is used
          const fileExists = !!profileImage.file;
          const fileUploadInProgress = uploadInProgress && fileExists;
          const delayAfterUpload = profileImage.imageId && this.state.uploadDelay;
          const imageFromFile =
            fileExists && (fileUploadInProgress || delayAfterUpload) ? (
              <ImageFromFile
                id={profileImage.id}
                className={errorClasses}
                rootClassName={css.uploadingImage}
                aspectRatioClassName={css.squareAspectRatio}
                file={profileImage.file}
              >
                {uploadingOverlay}
              </ImageFromFile>
            ) : null;

          // Avatar is rendered in hidden during the upload delay
          // Upload delay smoothes image change process:
          // responsive img has time to load srcset stuff before it is shown to user.
          const avatarClasses = classNames(errorClasses, css.avatar, {
            [css.avatarInvisible]: this.state.uploadDelay,
          });
          const avatarComponent =
            !fileUploadInProgress && profileImage.imageId ? (
              <Avatar
                className={avatarClasses}
                renderSizes="(max-width: 767px) 186px, 240px"
                user={transientUser}
                disableProfileLink
                disableVerifiedBadge
              />
            ) : null;

          const chooseAvatarLabel =
            profileImage.imageId || fileUploadInProgress ? (
              <div className={css.avatarContainer}>
                {imageFromFile}
                {avatarComponent}
                <div className={css.changeAvatar}>
                  <FormattedMessage id="ProfileSettingsForm.changeAvatar" />
                </div>
              </div>
            ) : (
              <div className={css.avatarPlaceholder}>
                <div className={css.avatarPlaceholderText}>
                  <FormattedMessage id="ProfileSettingsForm.addYourProfilePicture" />
                </div>
                <div className={css.avatarPlaceholderTextMobile}>
                  <FormattedMessage id="ProfileSettingsForm.addYourProfilePictureMobile" />
                </div>
              </div>
            );

          const submitError = updateProfileError ? (
            <div className={css.error}>
              <FormattedMessage id="ProfileSettingsForm.updateProfileFailed" />
            </div>
          ) : null;

          const classes = classNames(rootClassName || css.root, className);
          const submitInProgress = updateInProgress;
          const submittedOnce = Object.keys(this.submittedValues).length > 0;
          const pristineSinceLastSubmit = submittedOnce && isEqual(values, this.submittedValues);
          const submitDisabled =
            invalid ||
            pristineSinceLastSubmit ||
            uploadInProgress ||
            submitInProgress ||
            !this.profileImageChanged;

          return (
            <Form
              className={classes}
              onSubmit={e => {
                this.profileImageChanged = false;
                this.submittedValues = values;
                handleSubmit(e);
              }}
            >
              <div className={css.sidebar}>
                <div className={css.sectionContainer}>
                  {/* {deleteAvatar} */}
                  <Field
                    accept={ACCEPT_IMAGES}
                    id="profileImage"
                    name="profileImage"
                    label={chooseAvatarLabel}
                    type="file"
                    form={null}
                    uploadImageError={uploadImageError}
                    disabled={uploadInProgress}
                  >
                    {fieldProps => {
                      const { accept, id, input, label, disabled, uploadImageError } = fieldProps;
                      const { name, type } = input;
                      const onChange = e => {
                        const file = e.target.files[0];
                        form.change(`profileImage`, file);
                        form.blur(`profileImage`);
                        if (file != null) {
                          const tempId = `${file.name}_${Date.now()}`;
                          onImageUpload({ id: tempId, file });

                          this.imageReader.readAsDataURL(file);
                          this.setState({ isSelectionModalOpen: true });
                          this.profileImageChanged = true;
                        }
                      };
                      const onClick = e => {
                        e.target.value = null;
                      };

                      let error = null;

                      if (isUploadImageOverLimitError(uploadImageError)) {
                        error = (
                          <div className={css.error}>
                            <FormattedMessage id="ProfileSettingsForm.imageUploadFailedFileTooLarge" />
                          </div>
                        );
                      } else if (uploadImageError) {
                        error = (
                          <div className={css.error}>
                            <FormattedMessage id="ProfileSettingsForm.imageUploadFailed" />
                          </div>
                        );
                      }

                      return (
                        <div className={css.uploadAvatarWrapper}>
                          <label htmlFor={id}>{label}</label>
                          <input
                            accept={accept}
                            id={id}
                            name={name}
                            className={css.uploadAvatarInput}
                            disabled={disabled}
                            onChange={onChange}
                            type={type}
                            onClick={onClick}
                          />
                          {error}
                        </div>
                      );
                    }}
                  </Field>
                </div>
                {submitError}
                <Button
                  className={css.submitButton}
                  type="submit"
                  inProgress={submitInProgress}
                  disabled={submitDisabled}
                  ready={pristineSinceLastSubmit}
                >
                  <FormattedMessage id="ProfileSettingsForm.saveProfilePic" />
                </Button>
                {viewport.width >= MAX_MOBILE_SCREEN_WIDTH && (isValidCustomer || isValidProvider)
                  ? nameLocationWorkingMethod
                  : null}
                {viewport.width >= MAX_MOBILE_SCREEN_WIDTH && isValidProvider
                  ? codeOfConduct
                  : null}
                {viewport.width >= MAX_MOBILE_SCREEN_WIDTH && showContainers
                  ? hourlyFeeIdealCustomer
                  : null}
                {viewport.width >= MAX_MOBILE_SCREEN_WIDTH && showContainers ? topSkills : null}
                {viewport.width >= MAX_MOBILE_SCREEN_WIDTH && showContainers
                  ? toolsContainer
                  : null}
                {viewport.width >= MAX_MOBILE_SCREEN_WIDTH && showContainers ? hobbies : null}

                {viewport.width >= MAX_MOBILE_SCREEN_WIDTH && showContainers ? languages : null}
              </div>
              <div className={css.mainContent}>
                <div className={css.sectionContainer}>
                  {viewport.width >= MAX_MOBILE_SCREEN_WIDTH && (isValidCustomer || isValidProvider)
                    ? bioWhoAmI
                    : null}
                  {viewport.width >= MAX_MOBILE_SCREEN_WIDTH &&
                    showContainers &&
                    portfolioWorksContainer}

                  {viewport.width >= MAX_MOBILE_SCREEN_WIDTH && isValidProvider ? links : null}

                  {viewport.width <= MAX_MOBILE_SCREEN_WIDTH && showContainers
                    ? nameLocationWorkingMethod
                    : null}
                  {viewport.width <= MAX_MOBILE_SCREEN_WIDTH && showContainers
                    ? codeOfConduct
                    : null}
                  {viewport.width <= MAX_MOBILE_SCREEN_WIDTH && (isValidCustomer || isValidProvider)
                    ? bioWhoAmI
                    : null}
                  {viewport.width <= MAX_MOBILE_SCREEN_WIDTH &&
                    showContainers &&
                    portfolioWorksContainer}
                  {viewport.width <= MAX_MOBILE_SCREEN_WIDTH && isValidProvider ? links : null}
                  {showContainers && workExperience}
                  {showContainers && workedWith}
                  {showContainers && education}
                  {viewport.width <= MAX_MOBILE_SCREEN_WIDTH && showContainers ? topSkills : null}
                  {viewport.width <= MAX_MOBILE_SCREEN_WIDTH && showContainers
                    ? toolsContainer
                    : null}
                  {viewport.width <= MAX_MOBILE_SCREEN_WIDTH && showContainers ? hobbies : null}
                  {viewport.width <= MAX_MOBILE_SCREEN_WIDTH && showContainers ? languages : null}
                  {viewport.width <= MAX_MOBILE_SCREEN_WIDTH && showContainers
                    ? hourlyFeeIdealCustomer
                    : null}
                </div>

                <Modal
                  id="ProfileSettingsForm.selectImageArea"
                  contentClassName={css.selectImageAreaModalContent}
                  isOpen={this.state.isSelectionModalOpen}
                  onClose={() => this.setState({ isSelectionModalOpen: false })}
                  usePortal
                  onManageDisableScrolling={this.props.onManageDisableScrolling}
                >
                  <div className={css.cropImageWrapper}>
                    <CropImage
                      src={this.state.cropImageSrc}
                      onCropChange={this.onCropChange}
                      onImageLoaded={this.onImageLoaded}
                      crop={this.state.crop}
                      setCrop={crop => this.setState({ crop })}
                      completedCrop={this.state.completedCrop}
                      setCompletedCrop={completedCrop => this.setState({ completedCrop })}
                      imgRef={this.imgRef}
                      previewCanvasRef={this.previewCanvasRef}
                    />
                  </div>
                  <Button
                    className={css.submitImageButton}
                    type="button"
                    onClick={() => this.handleSubmitSelection()}
                    inProgress={false}
                    disabled={false}
                    ready={false}
                  >
                    <FormattedMessage id="ProfileSettingsForm.saveChanges" />
                  </Button>
                </Modal>
              </div>
              <Prompt when={this.state.formIsDirty} message={dirtyFormAlert} />
              <FormSpy subscription={{ dirty: true }} onChange={this.onDirtyChange} />
            </Form>
          );
        }}
      />
    );
  }
}

ProfileSettingsFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  uploadImageError: null,
  updateProfileError: null,
  updateProfileReady: false,
};

ProfileSettingsFormComponent.propTypes = {
  rootClassName: string,
  className: string,

  uploadImageError: propTypes.error,
  uploadInProgress: bool.isRequired,
  updateInProgress: bool.isRequired,
  updateProfileError: propTypes.error,
  updateProfileReady: bool,

  // from injectIntl
  intl: intlShape.isRequired,
};

const ProfileSettingsForm = compose(injectIntl)(ProfileSettingsFormComponent);

ProfileSettingsForm.displayName = 'ProfileSettingsForm';

export default ProfileSettingsForm;
